<template>
  <v-container fluid class="fill-height background-gradient">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card
            elevation="6"
            class="search-card"
            rounded="lg"
        >
          <v-card-title class="text-center pt-5 pb-2">
            <span class="text-h5 font-weight-medium primary--text">Buscar Cliente</span>
          </v-card-title>

          <v-card-text class="pa-5">
            <v-text-field
                v-model="termoBusca"
                label="Nome do cliente ou CPF"
                outlined
                rounded
                prepend-inner-icon="mdi-account-search"
                clearable
                color="primary"
                class="search-field"
                background-color="grey lighten-5"
                @keyup.enter="buscarCliente"
                hide-details="auto"
            ></v-text-field>

            <v-row class="mt-6">
              <v-col cols="6">
                <v-btn
                    block
                    color="primary"
                    rounded
                    elevation="2"
                    height="45"
                    class="text-capitalize font-weight-medium white--text"
                    @click="buscarCliente"
                    :loading="carregando"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Buscar
                </v-btn>
              </v-col>

              <v-col cols="6">
                <v-btn
                    block
                    color="secondary"
                    rounded
                    elevation="2"
                    height="45"
                    class="text-capitalize font-weight-medium white--text"
                    @click="cadastrarCliente"
                >
                  <v-icon left>mdi-account-plus</v-icon>
                  Cadastrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Lista de resultados -->
        <v-card
            v-if="mostrarResultados"
            elevation="6"
            class="results-card mt-4"
            rounded="lg"
        >
          <v-card-title class="d-flex justify-space-between align-center pa-4">
            <span class="font-weight-medium">Resultados da Busca</span>
            <v-chip color="primary" outlined pill small>
              {{ clientes.length }} cliente{{ clientes.length !== 1 ? 's' : '' }} encontrado{{ clientes.length !== 1 ? 's' : '' }}
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-0">
            <v-data-table
                :headers="cabecalhos"
                :items="clientes"
                :items-per-page="100"
                :search="searchTable"
                :loading="carregando"
                class="rounded-lg"
                :footer-props="{
                'items-per-page-options': [100, 150, 200],
                'items-per-page-text': 'Itens por página',
              }"
                no-data-text="Nenhum cliente encontrado"
                loading-text="Carregando clientes..."
            >
              <template #[`item.acoes`]="{ item }">
                <v-btn
                    icon
                    small
                    color="primary"
                    class="mr-1"
                    @click="visualizarCliente(item)"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                    icon
                    small
                    color="secondary"
                    class="mr-1"
                    @click="editarCliente(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>

              <template #[`item.status`]="{ item }">
                <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    pill
                >
                  {{ item.status }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card
            v-if="buscaRealizada && clientes.length === 0 && !carregando"
            elevation="6"
            class="results-card mt-4 pa-5 text-center"
            rounded="lg"
        >
          <v-icon size="60" color="grey lighten-1">mdi-account-search-outline</v-icon>
          <p class="text-body-1 mt-3 grey--text text--darken-1">Nenhum cliente encontrado com esses critérios de busca</p>
          <v-btn
              color="primary"
              text
              class="mt-2"
              @click="limparBusca"
          >
            Limpar busca
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Versículo do Dia - Modal Flutuante -->
    <v-slide-y-transition>
      <div
          class="versiculo-container"
          v-show="mostrarVersiculo"
          @mouseover="expandirVersiculo = true"
          @mouseleave="expandirVersiculo = false"
      >
        <v-card
            class="versiculo-card"
            :class="{ 'versiculo-expanded': expandirVersiculo }"
            rounded="lg"
            elevation="3"
        >
          <v-card-text class="py-2 px-3">
            <div class="d-flex align-center">
              <v-icon
                  color="primary"
                  class="versiculo-icon mr-2"
                  small
              >
                mdi-book-open-page-variant
              </v-icon>
              <span class="versiculo-titulo caption">Versículo do Dia</span>
              <v-spacer></v-spacer>
              <v-btn
                  icon
                  x-small
                  @click.stop="toggleVersiculo"
                  class="close-btn"
              >
                <v-icon x-small>{{ mostrarVersiculoCompleto ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="mostrarVersiculoCompleto || expandirVersiculo">
                <div class="versiculo-texto body-2 mt-2">
                  {{ versiculo.texto }}
                </div>
                <div class="versiculo-referencia caption mt-1 text-right primary--text">
                  {{ versiculo.referencia }}
                </div>
              </div>
            </v-expand-transition>
          </v-card-text>
        </v-card>
      </div>
    </v-slide-y-transition>
  </v-container>
</template>

<script>
import {clienteService} from "@/services";
import versiculosData from '@/assets/versiculos.json';

export default {
  name: 'BuscaCliente',

  data() {
    return {
      termoBusca: '',
      carregando: false,
      buscaRealizada: false,
      mostrarResultados: false,
      searchTable: '',
      clientes: [],
      cabecalhos: [
        { text: 'Nome', value: 'nome', sortable: true },
        { text: 'CPF/CNPJ', value: 'cpf', sortable: true },
        { text: 'Telefone', value: 'telefone', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Ações', value: 'acoes', sortable: false, align: 'center' }
      ],
      versiculo: {
        texto: "O Senhor é o meu pastor, nada me faltará.",
        referencia: "Salmos 23:1"
      },
      mostrarVersiculo: true,
      mostrarVersiculoCompleto: false,
      expandirVersiculo: false,
      carregandoVersiculo: false
    }
  },

  created() {
    this.buscarVersiculoDoDia();
  },

  methods: {
    async buscarCliente() {
      if (!this.termoBusca.trim()) {
        this.$toast.warning("Digite um nome ou CPF/CNPJ para busca");
        return;
      }

      this.carregando = true;
      this.buscaRealizada = true;

      try {
        this.clientes = await clienteService.buscarClientes(this.termoBusca)

        this.mostrarResultados = this.clientes.length > 0;
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        this.$toast.error('Erro ao buscar clientes. Tente novamente.');
      } finally {
        this.carregando = false;
      }
    },

    cadastrarCliente() {
      console.log('Navegando para tela de cadastro');
      this.$router.push({ name: 'CadastroCliente' });
    },

    visualizarCliente(cliente) {
      console.log('Visualizando cliente:', cliente);
      this.$router.push({ name: 'VisualizarCliente', params: { id: cliente.id } });
    },

    editarCliente(cliente) {
      console.log('Editando cliente:', cliente);
      // this.$router.push({ name: 'EditarCliente', params: { id: cliente.id } });
    },

    limparBusca() {
      this.termoBusca = '';
      this.buscaRealizada = false;
      this.mostrarResultados = false;
      this.clientes = [];
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'ativo':
          return 'success';
        case 'inativo':
          return 'grey';
        case 'pendente':
          return 'warning';
        case 'debito':
          return 'error';
        default:
          return 'grey';
      }
    },

    toggleVersiculo() {
      this.mostrarVersiculoCompleto = !this.mostrarVersiculoCompleto;
    },

    async buscarVersiculoDoDia() {
      this.carregandoVersiculo = true;

      const palavrasChave = [
        'amor',      // Domingo
        'esperança', // Segunda
        'fé',        // Terça
        'paz',       // Quarta
        'sabedoria', // Quinta
        'graça',     // Sexta
        'alegria'    // Sábado
      ];

      const diaDaSemana = new Date().getDay();
      const palavraChave = palavrasChave[diaDaSemana];

      try {
        const versiculos = versiculosData[palavraChave];

        if (versiculos && versiculos.length > 0) {
          const indiceAleatorio = Math.floor(Math.random() * versiculos.length);
          const versiculoSelecionado = versiculos[indiceAleatorio];

          this.versiculo = {
            texto: versiculoSelecionado.texto,
            referencia: versiculoSelecionado.referencia
          };
        } else {
          throw new Error('Nenhum versículo encontrado com esta palavra-chave');
        }
      } catch (error) {
        console.error('Erro ao buscar versículo do dia:', error);
        this.versiculo = {
          texto: "Este é o dia que o Senhor fez; alegremo-nos e regozijemo-nos nele.",
          referencia: "Salmos 118:24"
        };
      } finally {
        this.carregandoVersiculo = false;
      }
    }
  }
}
</script>

<style scoped>
.background-gradient {
  background: linear-gradient(135deg, #f8f9fa 0%, #f0e9eb 100%);
}

.search-card, .results-card {
  border-radius: 20px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07) !important;
}

.search-field {
  border-radius: 12px;
}

.v-text-field >>> .v-input__slot {
  min-height: 56px;
}

.v-btn {
  letter-spacing: 0.5px;
  text-transform: none;
  transition: all 0.2s ease;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.results-card {
  overflow: hidden;
}

/* Sutis detalhes de vinho/bordô na interface */
.v-card:hover {
  box-shadow: 0 12px 24px rgba(86, 22, 49, 0.09) !important;
}

.v-data-table >>> .v-data-table__wrapper {
  scrollbar-color: rgba(86, 22, 49, 0.2) transparent;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(86, 22, 49, 0.2);
  border-radius: 20px;
}

::-webkit-scrollbar-track {
  background-color: transparent;
}

/* Estilos para o componente de versículo */
.versiculo-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
  max-width: 300px;
  transition: all 0.3s ease;
}

.versiculo-card {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  overflow: hidden;
}

.versiculo-expanded {
  box-shadow: 0 8px 16px rgba(86, 22, 49, 0.12) !important;
  transform: translateY(-3px);
}

.versiculo-titulo {
  font-weight: 500;
  color: rgba(86, 22, 49, 0.8);
}

.versiculo-texto {
  font-style: italic;
  color: #424242;
  line-height: 1.4;
}

.versiculo-referencia {
  font-weight: 500;
}

.versiculo-icon {
  opacity: 0.7;
}

.close-btn {
  opacity: 0.6;
}

.close-btn:hover {
  opacity: 1;
}
</style>